import React, { useState, useEffect } from 'react';
import { 
  FiBook, FiAward, FiUser, FiBarChart2, FiStar, FiTarget,
  FiCheckCircle, FiTrendingUp, FiUsers, FiMessageSquare, FiZap
} from 'react-icons/fi';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { getLearnerMenu } from '../../components/layout/menuConfigs';
import EducationalParticles from '../../components/common/EducationalParticles';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { showToast } from '../../components/common/Toaster';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  learnerBadgeAPI,
} from '../../api/learnerEndpoints';
import { 
  DashboardContainer, 
  DashboardHeader, 
  StatsGrid, 
  StatCard, 
  StatIcon, 
  StatInfo, 
  SectionTitle,
  EmptyState,
  ContentGrid,
  ContentCard,
  ContentCardHeader,
  ContentCardBody,
} from '../dashboards/Dashboard.styles';

const BadgesPage = () => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [myBadges, setMyBadges] = useState([]);
  const [allBadges, setAllBadges] = useState([]);
  const [stats, setStats] = useState({});

  const menuItems = getLearnerMenu();

  useEffect(() => {
    fetchBadgeData();
  }, []);

  const fetchBadgeData = async () => {
    try {
      setLoading(true);
      const [myBadgesRes, allBadgesRes] = await Promise.all([
        learnerBadgeAPI.getMyBadges(),
        learnerBadgeAPI.getBadges(),
      ]);
      
      setMyBadges(myBadgesRes.data.badges || []);
      setAllBadges(allBadgesRes.data.badges || []);
      
      // Calculate stats
      const earnedBadges = myBadgesRes.data.badges?.length || 0;
      const totalBadges = allBadgesRes.data.badges?.length || 0;
      
      setStats({
        earnedBadges,
        totalBadges,
        completionRate: totalBadges > 0 ? (earnedBadges / totalBadges) * 100 : 0,
      });
    } catch (error) {
      showToast('error', 'Failed to load badges data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getBadgeIcon = (badgeType) => {
    const iconMap = {
      welcome: <FiStar />,
      achievement: <FiAward />,
      milestone: <FiTarget />,
    };
    return iconMap[badgeType] || <FiAward />;
  };

  const getBadgeColor = (badgeType) => {
    const colorMap = {
      welcome: '#2c5f34',
      achievement: '#F59E0B',
      milestone: '#10B981',
    };
    return colorMap[badgeType] || '#2c5f34';
  };

  const isBadgeEarned = (badgeId) => {
    return myBadges.some(badge => badge.badge_id === badgeId);
  };

  const getEarnedBadge = (badgeId) => {
    return myBadges.find(badge => badge.badge_id === badgeId);
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={menuItems}>
        <DashboardContainer>
          <LoadingSpinner size="large" text="Loading your badges..." />
        </DashboardContainer>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={menuItems}>
      <EducationalParticles />
      <DashboardContainer>
        <DashboardHeader>
          <div>
            <h1>My Badges 🏆</h1>
            <p>Showcase your learning achievements and milestones</p>
          </div>
        </DashboardHeader>

        {/* Badge Statistics */}
        <StatsGrid>
          <StatCard>
            <StatIcon color="#F59E0B">
              <FiAward />
            </StatIcon>
            <StatInfo>
              <h3>{stats.earnedBadges || 0}</h3>
              <p>Badges Earned</p>
            </StatInfo>
          </StatCard>

          <StatCard>
            <StatIcon color="#2c5f34">
              <FiTarget />
            </StatIcon>
            <StatInfo>
              <h3>{stats.totalBadges || 0}</h3>
              <p>Total Available</p>
            </StatInfo>
          </StatCard>

          <StatCard>
            <StatIcon color="#10B981">
              <FiTrendingUp />
            </StatIcon>
            <StatInfo>
              <h3>{stats.completionRate?.toFixed(1) || 0}%</h3>
              <p>Collection Rate</p>
            </StatInfo>
          </StatCard>
        </StatsGrid>

        {/* My Badges */}
        <SectionTitle>Earned Badges</SectionTitle>
        {myBadges.length === 0 ? (
          <EmptyState>
            <FiAward size={48} />
            <h3>No badges earned yet</h3>
            <p>Start learning to earn your first badge!</p>
          </EmptyState>
        ) : (
          <ContentGrid>
            {myBadges.map((badge) => (
              <ContentCard key={badge.id} style={{ 
                background: 'linear-gradient(135deg, #F59E0B15 0%, #F59E0B05 100%)',
                border: `2px solid ${getBadgeColor(badge.badge?.badge_type)}20`,
              }}>
                <ContentCardHeader>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ 
                      fontSize: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '48px',
                      height: '48px',
                      background: `${getBadgeColor(badge.badge?.badge_type)}15`,
                      borderRadius: '12px',
                    }}>
                      {badge.badge?.icon || getBadgeIcon(badge.badge?.badge_type)}
                    </div>
                    <div>
                      <h3 style={{ margin: 0, color: theme.colors.textPrimary }}>
                        {badge.badge?.name}
                      </h3>
                      <span 
                        className="badge"
                        style={{
                          background: `${getBadgeColor(badge.badge?.badge_type)}15`,
                          color: getBadgeColor(badge.badge?.badge_type),
                        }}
                      >
                        {badge.badge?.badge_type}
                      </span>
                    </div>
                  </div>
                </ContentCardHeader>
                <ContentCardBody>
                  <p style={{ margin: '0 0 12px 0' }}>
                    {badge.badge?.description}
                  </p>
                  <div className="meta">
                    <span>🏆 Earned on {formatDate(badge.earned_at)}</span>
                  </div>
                </ContentCardBody>
              </ContentCard>
            ))}
          </ContentGrid>
        )}

        {/* All Available Badges */}
        <SectionTitle style={{ marginTop: '40px' }}>All Available Badges</SectionTitle>
        <ContentGrid>
          {allBadges.map((badge) => {
            const isEarned = isBadgeEarned(badge.id);
            const earnedBadge = getEarnedBadge(badge.id);
            
            return (
              <ContentCard 
                key={badge.id} 
                style={{ 
                  opacity: isEarned ? 1 : 0.6,
                  background: isEarned 
                    ? `linear-gradient(135deg, ${getBadgeColor(badge.badge_type)}15 0%, ${getBadgeColor(badge.badge_type)}05 100%)`
                    : theme.colors.surface,
                  border: `2px solid ${isEarned ? getBadgeColor(badge.badge_type) + '40' : theme.colors.border}`,
                }}
              >
                <ContentCardHeader>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ 
                      fontSize: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '48px',
                      height: '48px',
                      background: isEarned 
                        ? `${getBadgeColor(badge.badge_type)}15` 
                        : '#E5E7EB',
                      borderRadius: '12px',
                    }}>
                      {isEarned ? (badge.icon || getBadgeIcon(badge.badge_type)) : '🔒'}
                    </div>
                    <div>
                      <h3 style={{ 
                        margin: 0, 
                        color: isEarned ? theme.colors.textPrimary : theme.colors.textSecondary 
                      }}>
                        {badge.name}
                      </h3>
                      <span 
                        className="badge"
                        style={{
                          background: isEarned 
                            ? `${getBadgeColor(badge.badge_type)}15`
                            : '#E5E7EB',
                          color: isEarned 
                            ? getBadgeColor(badge.badge_type)
                            : theme.colors.textSecondary,
                        }}
                      >
                        {isEarned ? 'Earned' : 'Locked'}
                      </span>
                    </div>
                  </div>
                </ContentCardHeader>
                <ContentCardBody>
                  <p style={{ 
                    margin: '0 0 12px 0',
                    color: isEarned ? theme.colors.textPrimary : theme.colors.textSecondary 
                  }}>
                    {badge.description}
                  </p>
                  <div className="meta">
                    {isEarned && earnedBadge ? (
                      <span>🏆 Earned on {formatDate(earnedBadge.earned_at)}</span>
                    ) : (
                      <span>🔒 Complete requirements to unlock</span>
                    )}
                  </div>
                </ContentCardBody>
              </ContentCard>
            );
          })}
        </ContentGrid>
      </DashboardContainer>
    </DashboardLayout>
  );
};

export default BadgesPage;
